import { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/error-handler';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ActivityWithRelations } from "@/types/activity.types";
import { OrganizationBasic } from "@/types/organization.types";
import { ContactBasic } from "@/types/contact.types";
import { OpportunityBasic } from "@/types/opportunity.types";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Calendar as CalendarIcon,
  Check,
  ChevronsUpDown,
  Clock,
  MapPin,
  UserCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchOrganizations } from '@/services/organization/organizationService';
import { fetchContacts } from '@/services/contact/contactService';
import { fetchOpportunities } from '@/services/opportunity/opportunityService';
import { interactionsService } from '@/services/relationship/interactionsService';

const interactionTypes = [
  { value: 'meeting', label: 'Meeting', icon: '👥' },
  { value: 'call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'message', label: 'Message', icon: '💬' },
  { value: 'task', label: 'Task', icon: '✓' },
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'other', label: 'Other', icon: '📌' },
];

const directions = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'internal', label: 'Internal' },
];

export function EnhancedActivityForm({ existingActivity }: { existingActivity?: ActivityWithRelations }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Pre-populate entity from URL params
  const initialOrganization = searchParams.get('organization');
  const initialContact = searchParams.get('contact');
  const initialOpportunity = searchParams.get('opportunity');

  // Form state
  const [interactionType, setInteractionType] = useState(
    existingActivity?.interaction_type || 'meeting'
  );
  const [direction, setDirection] = useState(existingActivity?.direction || 'outbound');
  const [notes, setNotes] = useState(existingActivity?.notes || '');
  const [date, setDate] = useState<Date>(
    existingActivity ? new Date(existingActivity.created_at) : new Date()
  );
  const [duration, setDuration] = useState(existingActivity?.duration || '');
  const [location, setLocation] = useState(existingActivity?.metadata?.location || '');
  const [attendees, setAttendees] = useState(existingActivity?.metadata?.attendees || '');
  const [outcome, setOutcome] = useState(existingActivity?.outcome || '');
  const [nextSteps, setNextSteps] = useState(existingActivity?.metadata?.next_steps || '');

  // Entity selection state
  const [entityType, setEntityType] = useState<'organization' | 'contact' | 'opportunity'>(
    initialOrganization
      ? 'organization'
      : initialContact
        ? 'contact'
        : initialOpportunity
          ? 'opportunity'
          : 'organization'
  );
  const [selectedEntityId, setSelectedEntityId] = useState(
    initialOrganization || initialContact || initialOpportunity || existingActivity?.entity_id || ''
  );

  // Entity search state
  const [organizations, setOrganizations] = useState<OrganizationBasic[]>([]);
  const [contacts, setContacts] = useState<ContactBasic[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityBasic[]>([]);
  const [entitySearchOpen, setEntitySearchOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEntities, setIsLoadingEntities] = useState(true);

  // Load entities for selection
  const loadEntities = useCallback(async () => {
    setIsLoadingEntities(true);
    try {
      const [orgsData, contactsData, oppsData] = await Promise.all([
        fetchOrganizations().catch((err) => {
          console.warn('Failed to load organizations:', err);
          return [];
        }),
        fetchContacts().catch((err) => {
          console.warn('Failed to load contacts:', err);
          return [];
        }),
        fetchOpportunities().catch((err) => {
          console.warn('Failed to load opportunities:', err);
          return [];
        }),
      ]);

      setOrganizations(orgsData || []);
      setContacts(contactsData || []);
      setOpportunities(oppsData || []);
      
      console.log('Entities loaded:', {
        organizations: orgsData?.length || 0,
        contacts: contactsData?.length || 0,
        opportunities: oppsData?.length || 0,
      });
    } catch (error) {
      logError('Error loading entities:', error);
      toast({
        title: 'Warning',
        description: 'Unable to load entity data. Please check your connection and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEntities(false);
    }
  }, [toast]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const getEntityOptions = () => {
    switch (entityType) {
      case 'organization':
        return organizations.map((org) => ({
          value: org.id,
          label: org.name,
          description: org.industry,
        }));
      case 'contact':
        return contacts.map((contact) => ({
          value: contact.id,
          label: `${contact.first_name} ${contact.last_name}`,
          description: contact.email,
        }));
      case 'opportunity':
        return opportunities.map((opp) => ({
          value: opp.id,
          label: opp.name,
          description: `${opp.stage} - $${opp.value?.toLocaleString() || 0}`,
        }));
      default:
        return [];
    }
  };

  const getSelectedEntityLabel = () => {
    const options = getEntityOptions();
    const selected = options.find((opt) => opt.value === selectedEntityId);
    return selected?.label || 'Select entity...';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEntityId || !interactionType) {
      toast({
        title: 'Error',
        description: 'Please select an entity and interaction type',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const interactionData = {
        entity_type: entityType,
        entity_id: selectedEntityId,
        interaction_type: interactionType,
        direction,
        notes,
        outcome,
        duration: duration ? parseInt(duration) : null,
        metadata: {
          location,
          attendees: attendees
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean),
          next_steps: nextSteps,
          logged_at: date.toISOString(),
        },
      };

      if (existingActivity) {
        await interactionsService.updateInteraction(existingActivity.id, interactionData);
        toast({
          title: 'Success',
          description: 'Activity updated successfully',
        });
      } else {
        await interactionsService.createInteraction(interactionData);
        toast({
          title: 'Success',
          description: 'Activity logged successfully',
        });
      }

      // Navigate back to the entity page
      if (entityType === 'organization') {
        navigate(`/organizations/${selectedEntityId}`);
      } else if (entityType === 'contact') {
        navigate(`/contacts/${selectedEntityId}`);
      } else if (entityType === 'opportunity') {
        navigate(`/opportunities/${selectedEntityId}`);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save activity',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{existingActivity ? 'Edit' : 'Log'} Activity</CardTitle>
          <CardDescription>
            Record interactions and activities with your contacts, organizations, and opportunities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Entity Selection */}
          <div className="space-y-4">
            <div>
              <Label>Related To</Label>
              <Tabs value={entityType} onValueChange={(v) => setEntityType(v as 'organization' | 'contact' | 'opportunity')}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="organization">Organization</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                  <TabsTrigger value="opportunity">Opportunity</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div>
              <Label>Select {entityType}</Label>
              <Popover open={entitySearchOpen} onOpenChange={setEntitySearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={entitySearchOpen}
                    className="w-full justify-between"
                    disabled={isLoadingEntities}
                  >
                    {isLoadingEntities ? 'Loading...' : getSelectedEntityLabel()}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder={`Search ${entityType}...`} />
                    <CommandEmpty>
                      {isLoadingEntities 
                        ? `Loading ${entityType}s...` 
                        : `No ${entityType}s found. ${getEntityOptions().length === 0 ? 'Please ensure you have access to view data.' : ''}`
                      }
                    </CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                      {getEntityOptions().map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={(value) => {
                            setSelectedEntityId(value);
                            setEntitySearchOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedEntityId === option.value ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{option.label}</div>
                            {option.description && (
                              <div className="text-sm text-muted-foreground">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Activity Details */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Activity Type</Label>
              <Select value={interactionType} onValueChange={setInteractionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {interactionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {directions.map((dir) => (
                    <SelectItem key={dir.value} value={dir.value}>
                      {dir.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date & Time</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !date && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP HH:mm') : 'Select date and time'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                  />
                  <div className="p-3 border-t">
                    <Input
                      type="time"
                      value={format(date, 'HH:mm')}
                      onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(':');
                        const newDate = new Date(date);
                        newDate.setHours(parseInt(hours, 10));
                        newDate.setMinutes(parseInt(minutes, 10));
                        setDate(newDate);
                      }}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Duration (minutes)</Label>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="30"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
              </div>
            </div>

            {(interactionType === 'meeting' || interactionType === 'call') && (
              <>
                <div>
                  <Label>Location</Label>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Office, Zoom, Phone..."
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label>Attendees</Label>
                  <div className="flex items-center space-x-2">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="John Doe, Jane Smith..."
                      value={attendees}
                      onChange={(e) => setAttendees(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Notes and Outcome */}
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Add details about this activity..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-24"
              />
            </div>

            <div>
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="successful">Successful</SelectItem>
                  <SelectItem value="follow_up_required">Follow-up Required</SelectItem>
                  <SelectItem value="no_response">No Response</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Next Steps</Label>
              <Textarea
                placeholder="What are the next actions to take?"
                value={nextSteps}
                onChange={(e) => setNextSteps(e.target.value)}
                className="min-h-20"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-4">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : existingActivity ? 'Update Activity' : 'Log Activity'}
        </Button>
      </div>
    </form>
  );
}
